import Page from "components/page/Page";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { ButtonNew as Button } from "components/buttonNew";
import Card from "components/card/Card";
import "./websiteBuilderOverview.scss";
import { Badge } from "components/badge/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { faCircleCheck, faEllipsisVertical, faPalette, IconName } from "@fortawesome/pro-regular-svg-icons";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWebsite } from "../shared/hooks/useWebsite";
import { useHistory } from "react-router";
import { WebsiteBuilderLoadingContent } from "./components/loadingContent/WebsiteBuilderLoadingContent";
import Callout from "components/callout/Callout";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import { DeleteWebsiteBuilderWebsite, UnpublishWebsiteBuilderWebsite } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useTranslation } from "react-i18next";

export default function WebsiteBuilderOverview() {
  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();

  const history = useHistory();

  const facilityStore = useAppSelector(store => store.facilityStore);
  
  const {
    website,
    websiteLoadingStatus,
    websiteExists,
    setWebsite,
  } = useWebsite({ websiteMustExist: false });

  const websiteDomain = website?.domain;
  const websitePublished = website?.published_version_id != null;

  const windowSize = useWindowSize();

  const newWebsiteSectionRef = useRef<HTMLDivElement>(null);
  const newWebsiteSectionDebounceRef = useRef<NodeJS.Timeout>(null);
  const [newWebsiteSectionPosition, setNewWebsiteSectionPosition] = useState<number>(0);

  const newWebsiteSectionHeight = windowSize.height - newWebsiteSectionPosition - 1;
  const newWebsiteSectionHeightActive = !websiteExists && windowSize.height > 600;

  const [completeWebsiteCalloutHidden, setCompleteWebsiteCalloutHidden] = useState<boolean>(false);

  const { state: deleteWebsiteModal, updateModal: updateDeleteWebsiteModal, closeModal: closeDeleteWebsiteModal } = useModal();

  useLayoutEffect(() => {
    if (websiteLoadingStatus !== "succeeded" || websiteExists || !newWebsiteSectionRef.current) {
      return;
    }

    const update = () => setNewWebsiteSectionPosition(newWebsiteSectionRef.current.getBoundingClientRect().y);

    update();

    const debouncedUpdate = () => {
      clearTimeout(newWebsiteSectionDebounceRef.current);

      newWebsiteSectionDebounceRef.current = setTimeout(update, 100);
    };

    const resizeObserver = new ResizeObserver(debouncedUpdate);

    resizeObserver.observe(newWebsiteSectionRef.current);
    return () => {
      clearTimeout(newWebsiteSectionDebounceRef.current);
      resizeObserver.disconnect();
    };
  }, [websiteLoadingStatus, websiteExists]);

  function renderNewWebsiteSection() {
    return (
      <div
        ref={newWebsiteSectionRef}
        className="new-website-section"
        style={{
          height: newWebsiteSectionHeightActive ? newWebsiteSectionHeight : null,
        }}
      >
        <img
          className="new-website-section-image"
          src="/public/images/websiteBuilder/config/overview/website_builder_illustration.png"
          alt={t("Website Builder Illustration")}
        />
        <div>
          <div className="new-website-section-message">{t("Start building your website")}</div>
          <div className="new-website-section-supporting-message">
            {t("Ready to bring your website to life? Start by choosing a template and customize it to fit your vision.")}
          </div>
        </div>
        <Button type="quaternary" onClick={launchWebsiteBuilderEditor}>
          {t("Get started")}
        </Button>
      </div>
    );
  }

  function renderCompleteWebsiteCallout() {
    const logoAndBrandCompleted = website.logo_url != null && website.favicon_url != null;
    
    const showCompleteWebsiteCallout =
      !completeWebsiteCalloutHidden &&
      (!logoAndBrandCompleted || !websitePublished);

    if (showCompleteWebsiteCallout) {
      return (
        <Callout
          type="primary"
          title={t("Next steps to complete your site")}
          onClose={() => setCompleteWebsiteCalloutHidden(true)}
          content={
            <div className="complete-website-callout-content">
              <p>{t("You've launched your website—great start! Now make it shine with these finishing touches:")}</p>
              <ul>
                {!logoAndBrandCompleted && <li>{t("Upload your company logo and favicon")}</li>}
                {!websitePublished && <li>{t("Publish your website")}</li>}
              </ul>
            </div>
          }
          actions={{
            primary: {
              label: !logoAndBrandCompleted ? t("Go to website logo and brand") : t("View your website"),
              onClick: () => !logoAndBrandCompleted ? history.push("/admin/website-builder-logo-and-brand") : history.push("/admin/website-builder-editor"),
            },
          }}
        />
      );
    } else {
      return null;
    }
  }

  async function handleUnpublishSite() {
    const res = await UnpublishWebsiteBuilderWebsite({ id: website.id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error unpublishing site")));
      return;
    }
    
    setWebsite(res.data);

    dispatch(showSuccess(t("Successfully unpublished site")));
  }

  function renderManageWebsiteSection() {
    return (
      <div className="manage-website-section">
        {renderCompleteWebsiteCallout()}
        <Card
          className="manage-website-section-outer-card"
          title={t("Website overview")}
          subtitle={t("Manage your website and its pages")}
        >
          <Card.Section>
            <div className="manage-website-section-card-container">
              <Card>
                <Card.Section>
                  <div className="manage-website-section-card-content">
                    <div className="manage-website-section-thumbnail-container">
                      <img
                        className="manage-website-section-thumbnail"
                        src={website.thumbnail_url}
                      />
                    </div>
                    <div className="manage-website-section-card-information">
                      <div className="manage-website-section-title-container">
                        <div className="manage-website-section-title">{facilityStore.facility?.long_name}</div>
                        <Badge
                          iconLeft={<FontAwesomeIcon className="manage-website-section-status-icon" icon={faCircle} />}
                          type={websitePublished ? "success": "gray"}
                        >
                          {websitePublished ? t("Published") : t("Draft")}
                        </Badge>
                      </div>
                      <hr className="manage-website-section-divider" />
                      <div className="manage-website-section-domain-container">
                        <div className="manage-website-section-domain-label">{t("Domain:")}</div>
                        {websiteDomain ? (
                          <>
                            <a
                              className="manage-website-section-domain"
                              href={`https://${websiteDomain}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {websiteDomain}
                            </a>
                            <FontAwesomeIcon className="manage-website-section-domain-active" icon={faCircleCheck} />
                          </>
                        ) : (
                          <div className="manage-website-section-domain">{t("Not linked")}</div>
                        )}
                      </div>
                    </div>
                    <NavigationDropdownNew
                      hideChevron
                      label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                      leftAlign
                      sections={[
                        [
                          {
                            label: t("Edit site"),
                            icon: "pen-to-square",
                            onClick: () => history.push("/admin/website-builder-editor"),
                          },
                          ...(websitePublished ? [{
                            label: t("Unpublish site"),
                            icon: "eye-slash" as IconName,
                            onClick: handleUnpublishSite,
                          }] : []),
                          {
                            label: t("Site settings"),
                            icon: "gear",
                            onClick: () => history.push("/admin/website-builder-settings"),
                          },
                        ],
                        [
                          {
                            label: t("Delete"),
                            icon: "trash",
                            color: "red",
                            onClick: () => updateDeleteWebsiteModal({ isOpen: true }),
                          },
                        ],
                      ]}
                    />
                  </div>
                </Card.Section>
              </Card>
            </div>
          </Card.Section>
        </Card>
      </div>
    );
  }

  function renderOverview() {
    if (websiteLoadingStatus === "succeeded") {
      if (websiteExists) {
        return renderManageWebsiteSection();
      } else {
        return renderNewWebsiteSection();
      }
    } else {
      return null;
    }
  }

  const launchWebsiteBuilderEditorLabel =
    <>
      <FontAwesomeIcon icon={faPalette} className="mr-2" />
      {websiteExists ? t("Customize website") : t("Create new website")}
    </>;

  function launchWebsiteBuilderEditor() {
    if (websiteExists) {
      history.push("/admin/website-builder-editor");
    } else {
      history.push("/admin/website-builder/templates");
    }
  }

  async function handleDeleteWebsite() {
    const res = await DeleteWebsiteBuilderWebsite({ id: website.id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error deleting website")));
      
      return;
    }

    closeDeleteWebsiteModal();
    setWebsite(null);

    dispatch(showSuccess("Successfully deleted website"));
  }

  return (
    <Page
      id="website-builder-overview-page"
      className={!websiteExists ? "overview-page-no-padding" : null}
      narrow
      title={t("Overview")}
      subtitle={t("Manage your website")}
      primaryAction={{
        content: launchWebsiteBuilderEditorLabel,
        action: launchWebsiteBuilderEditor,
        disabled: websiteLoadingStatus !== "succeeded",
      }}
    >
      <WebsiteBuilderLoadingContent loading={websiteLoadingStatus === "standby" || websiteLoadingStatus === "loading"}>
        {renderOverview()}
      </WebsiteBuilderLoadingContent>
      <Portal isMounted={deleteWebsiteModal.isOpen}>
        <Sheet
          title={t("Delete Website")}
          open={deleteWebsiteModal.isOpen}
          size="small"
          closable
          onCancel={closeDeleteWebsiteModal}
          onOk={handleDeleteWebsite}
          okText={t("Delete")}
        >
          <div>{t("Are you sure you want to delete your website? This action can't be reversed.")}</div>
        </Sheet>
      </Portal>
    </Page>
  );
}
